import type { Key } from "@react-types/shared";
import { modify, optic } from "optics-ts";
import { append, findIndex, reject } from "ramda";
import { createContext, type RefObject, useContext } from "react";
import { createStore, type StateCreator, type StoreApi } from "zustand";
import { useStoreWithEqualityFn } from "zustand/traditional";

import type { PanelApi } from "../PanelGroup.tsx";
import type { Tab } from "../types.ts";

interface PanelLayoutState {
  tabs: Tab[];
  activeTabId: Key | null;
  showFixedPanel: boolean;

  /** Use it with care and only if you don't use the store afterwards */
  panelApiRef: PanelApi | null;
}

interface PanelLayoutActions {
  setPanelApiRef: (apiRef: RefObject<PanelApi>) => void;
  setActiveTabId: (tabId: Key) => void;
  addTab: (tab: Tab) => void;
  removeTab: (tabId: Tab["id"]) => void;
  togglePanel: () => void;
}

export type PanelLayoutStore = PanelLayoutState & PanelLayoutActions;

const initialState: PanelLayoutState = {
  tabs: [],
  activeTabId: null,
  showFixedPanel: true,
  panelApiRef: null,
};

const panelLayoutStateCreator: StateCreator<PanelLayoutStore> = (set, get) => ({
  ...initialState,
  setPanelApiRef(apiRef) {
    // TODO: Isn't that simpler ?
    // apiRef.current?.onMoveEnd((_, isClosed) => {
    //   set({ showFixedPanel: !isClosed });
    // });

    apiRef.current?.onMove((size, isClosed) => {
      const isOpen = get().showFixedPanel;
      if (size > 0) {
        // isOpen is more wasOpen and isClosed is more hasBeenClosed or willClose
        if (!isOpen && !isClosed) {
          set({ showFixedPanel: true });
        } else if (isOpen && isClosed) {
          set({ showFixedPanel: false });
        }
      }
    });

    set({ panelApiRef: apiRef.current });
  },
  addTab(tab) {
    const tabs = get().tabs;
    const idx = findIndex((t) => t.id === tab.id, tabs);

    if (idx >= 0) {
      // Just update
      set(modify(optic<PanelLayoutState>().prop("tabs").at(idx))((item) => ({ ...item, ...tab })));
    } else {
      // Push New Tab
      set(modify(optic<PanelLayoutState>().prop("tabs"))((tabs) => append(tab, tabs)));
    }
  },
  removeTab(tabId) {
    const tabs = reject<Tab, Tab[]>((tab) => tab.id === tabId, get().tabs);
    set({ tabs });
  },
  setActiveTabId(tabId) {
    set({ activeTabId: tabId });
  },
  togglePanel() {
    const api = get().panelApiRef;

    api?.toggle((isOpen) => {
      set({ showFixedPanel: isOpen });
    });
  },
});

export const createPanelLayoutStore = (defaultOpen?: boolean) => {
  const store = createStore<PanelLayoutStore>(panelLayoutStateCreator);

  if (defaultOpen != null && initialState.showFixedPanel !== defaultOpen) {
    store.setState({ showFixedPanel: defaultOpen });
  }

  return store;
};

export const PanelLayoutContext = createContext<StoreApi<PanelLayoutStore>>({} as StoreApi<PanelLayoutStore>);

export function usePanelLayoutStore<T>(selector: (state: PanelLayoutStore) => T, equals?: (a: T, b: T) => boolean): T {
  return useStoreWithEqualityFn(useContext(PanelLayoutContext), selector, equals);
}
